import useIsMobile from 'utils/hooks/useIsMobile'
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation'
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow'
import ListAltIcon from '@mui/icons-material/ListAlt'
import RotateLeftTwoToneIcon from '@mui/icons-material/RotateLeftTwoTone'
import SearchIcon from '@mui/icons-material/Search'
import {
  Backdrop,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import {DataGridPro, GridColDef} from '@mui/x-data-grid-pro'
import {DateRangePicker, LocalizationProvider} from '@mui/x-date-pickers-pro'
import {AdapterDayjs} from '@mui/x-date-pickers-pro/AdapterDayjs'
import {DateRange} from '@mui/x-date-pickers-pro/DateRangePicker'
import {noImage, yMarker} from 'assets/imageDeclear'
import LazyImage from 'utils/designSytem/LazyImage'
import dayjs, {Dayjs} from 'dayjs'
import {envBackHost, envImagePath} from 'GLOBAL/envVariables'
import {axiosWithAuth} from 'helper/axiosWithAuth'
import fileDownload from 'js-file-download'
import {customerTargetListRows} from 'pages/customer/dataConfig/declearRows'
import React, {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {globalConst, globalSerialNumberFormatWithHyphens} from 'utils/const/globalConst'
import {formatDateOrEmpty} from 'utils/func/justUtils'
import {resultAlert} from '../../utils/CustomAlert/Alert'
import {CustomerDetailChartFix} from './CustomerDetailChartFixed'

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  height: '90vh',
  bgcolor: 'background.paper',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  boxShadow: 24,
  p: {xs: 1, md: 4},
}

const DeviceTargetImageListModal = (props: any) => {
  const {data, open, onClose} = props
  const isMobile = useIsMobile()
  const [date, setDate] = React.useState<DateRange<Dayjs>>([null, null])
  const [pageSize, setPageSize] = useState<number>(60)
  const [imageList, setImageList] = useState<any>(null)
  const [selectedOption, setSelectedOption] = useState<any>('reading')
  const userInfo = useSelector((state: any) => state?.userInfo?.userInfo)
  const [filter, setFilter] = useState<any>({
    startDate: dayjs().subtract(7, 'day').format('YYYYMMDD'),
    endDate: dayjs().format('YYYYMMDD'),
  })
  const [mobilePage, setMobilePage] = useState(0)
  const mobileListRef = useRef<HTMLDivElement>(null)

  const getImageList = () => {
    axiosWithAuth()
      .get(`${envBackHost}/device/${data?.imei}/checkmeter`, {params: filter})
      .then((res) => {
        setImageList(res.data.res)
        setMobilePage(0)
      })
  }

  const getDownloadData = async () => {
    axiosWithAuth()
      .get(
        `${envBackHost}/device/${data?.imei}/checkmeter/excel?startDate=${filter.startDate}&endDate=${filter?.endDate}`,
        {responseType: 'blob'}
      )
      .then(async (res) => {
        await resultAlert({resultCode: 0})
        fileDownload(
          res.data,
          `${data?.serialNumber}단말기_${filter.startDate}~${filter.endDate}_검침기록.xlsx`
        )
        await document.getElementById('downloadModalClose')?.click()
      })
      .catch(async (e) => {
        await resultAlert({resultCode: e.response.status})
      })
  }

  const columns: GridColDef[] = [
    {
      field: 'createAt',
      headerName: '검침일시',
      width: 260,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => (
        <div>{formatDateOrEmpty(params?.row?.createAt, 'YYYY-MM-DD HH시 mm분 ss초')}</div>
      ),
    },
    {
      field: 'filePath',
      headerName: '검침 이미지',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      hide: data?.deviceType === 'digital',
      renderCell: (params: any) => (
        <div style={{width: 220}}>
          <LazyImage
            src={params?.row?.filePath ? envImagePath + params.row.filePath : null}
            alt='검침 이미지'
            width={220}
            fallback={<img src={noImage} alt='no image' style={{width: '100%'}} />}
            style={{
              transform: `rotate(${params?.row?.rotate >= 90 && params?.row?.rotate <= 270 ? 180 : 0}deg)`,
            }}
          />
        </div>
      ),
    },
    {
      field: 'signalStrength',
      headerName: '전파세기',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const signalStrength = params.row.signalStrength
        const isWeakSignal = signalStrength < globalConst.rssiWarningdBm
        return (
          <div className={`flex justify-center ${isWeakSignal ? 'text-orange-500 font-bold' : ''}`}>
            {signalStrength !== null ? `${signalStrength}dbm` : `-`}
          </div>
        )
      },
    },
    {
      field: 'signalQuality',
      headerName: '전파품질',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const linkquality = params.row.linkquality
        const isWeakQuality = linkquality !== null && linkquality < globalConst.rsrqGood
        return (
          <div
            className={`flex justify-center ${isWeakQuality ? 'text-orange-500 font-bold' : ''}`}
          >
            {linkquality !== null ? `${linkquality}dB` : `-`}
          </div>
        )
      },
    },
  ]

  const getRowHeightOnDataGrid = (params: any) => {
    switch (params?.model?.recognizedType) {
      case 'PRV':
        return 200
      default:
        return 110
    }
  }

  const rows = customerTargetListRows(imageList)

  const deviceFields = [
    {label: '단말기 S/N', value: globalSerialNumberFormatWithHyphens(data?.serialNumber)},
    {label: '단말기 IMEI', value: data?.imei},
    {label: 'PSN', value: data?.psn},
    {label: '배터리', value: data?.batteryLevel + '%'},
    {
      label: '설치자',
      value: !window.location.pathname?.includes('info')
        ? '정보없음'
        : `${data?.installerId}(${data?.installerName})`,
    },
    {label: '신호강도', value: data?.signalStrength + 'dBm'},
  ]

  useEffect(() => {
    getImageList()
  }, [data, filter?.startDate, filter?.endDate])

  useEffect(() => {
    const today = dayjs()
    const oneWeekAgo = today.subtract(7, 'day')
    setDate([oneWeekAgo, today])
  }, [])

  useEffect(() => {
    if (data && data.latitude && data.longitude) {
      setTimeout(() => {
        let container = document?.getElementById('map')
        if (container) {
          const map = new window.kakao.maps.Map(container, {
            center: new window.kakao.maps.LatLng(data.latitude, data.longitude),
            mapTypeId: window.kakao.maps.MapTypeId.HYBRID,
            level: 1,
            draggable: false,
            scrollwheel: false,
            zoomable: false,
          })
          const mapTypeControl = new window.kakao.maps.MapTypeControl()
          map.addControl(mapTypeControl, window.kakao.maps.ControlPosition.TOPRIGHT)
          const markerPosition = new window.kakao.maps.LatLng(data.latitude, data.longitude)
          const marker = new window.kakao.maps.Marker({
            position: markerPosition,
            image: new window.kakao.maps.MarkerImage(yMarker, new window.kakao.maps.Size(40, 59)),
          })
          marker.setMap(map)
        }
      }, 300)
    }
  }, [data])

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose()
        setImageList(null)
      }}
      aria-labelledby={`${window.location.pathname}-label`}
      aria-describedby={`${window.location.pathname}-description`}
      closeAfterTransition
      slots={{backdrop: Backdrop}}
      slotProps={{backdrop: {timeout: 500}}}
    >
      <Box sx={style}>
        <div className='flex flex-col h-full min-h-0'>
          {/* 헤더 */}
          <div className='flex justify-between items-center mb-2'>
            <Chip
              label={`S/N ${globalSerialNumberFormatWithHyphens(data?.serialNumber)} 상세정보`}
              variant='outlined'
              sx={{fontSize: isMobile ? '14px' : '20px', fontWeight: 'bold'}}
            />
            <CancelPresentationIcon
              sx={{fontSize: '40px', cursor: 'pointer'}}
              color='error'
              onClick={() => {
                onClose()
                setImageList(null)
              }}
            />
          </div>

          {/* 바디 */}
          <div className='flex-1 min-h-0 overflow-y-auto lg:overflow-hidden'>
            {data && (
              <div className='flex flex-col lg:flex-row gap-4 lg:h-full'>
                {/* 왼쪽: 단말기 정보 */}
                <div className='w-full lg:w-1/4 min-w-0 flex flex-col lg:h-full'>
                  <div className='border border-gray-400 shadow-sm flex flex-col flex-1 min-h-0'>
                    <Paper className='p-4 border-b border-gray-400' square>
                      <span className='font-semibold text-xl'>단말기 정보</span>
                    </Paper>
                    <Paper
                      className='p-4'
                      square
                      style={{flex: 1, overflow: 'auto', minHeight: 0}}
                      sx={!isMobile ? {'& .MuiInputBase-root': {height: 48}} : {}}
                    >
                      <div className='flex flex-col gap-2'>
                        {deviceFields.map(({label, value}) => (
                          <div key={label} className='flex flex-col gap-1'>
                            <span className='flex items-center gap-1 font-semibold text-base'>
                              <DoubleArrowIcon /> {label}
                            </span>
                            <TextField
                              variant='outlined'
                              size='small'
                              className='bg-gray-300'
                              value={value}
                              inputProps={{readOnly: true}}
                            />
                          </div>
                        ))}
                      </div>
                    </Paper>
                  </div>
                </div>

                {/* 오른쪽: 필터 + 차트 + 테이블 */}
                <div className='flex-1 min-w-0 flex flex-col gap-2 lg:h-full lg:min-h-0'>
                  {/* 필터 바 */}
                  <div
                    className={`flex flex-wrap gap-2 items-center ${isMobile ? 'py-1' : 'py-3'}`}
                  >
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      localeText={{start: '시작날짜', end: '마지막 날짜'}}
                    >
                      <DateRangePicker
                        PopperProps={{style: {zIndex: 9999}}}
                        value={date}
                        calendars={3}
                        mask='____/__/__'
                        inputFormat={'YYYY/MM/DD'}
                        onChange={(newValue) => {
                          setDate(newValue)
                          setFilter({
                            ...filter,
                            startDate: newValue[0]?.format('YYYYMMDD'),
                            endDate: newValue[1]?.format('YYYYMMDD'),
                          })
                        }}
                        renderInput={(startProps, endProps) => (
                          <div className='flex items-center gap-2'>
                            <TextField
                              {...startProps}
                              size='small'
                              autoComplete='off'
                              InputLabelProps={{
                                shrink: true,
                                style: {fontSize: isMobile ? '1.2rem' : '1.5rem'},
                              }}
                              InputProps={{
                                ...startProps.InputProps,
                                sx: {height: isMobile ? 36 : 48, fontSize: isMobile ? '1.2rem' : '1.5rem'},
                              }}
                            />
                            <span className='text-gray-500 font-semibold'>~</span>
                            <TextField
                              {...endProps}
                              size='small'
                              autoComplete='off'
                              InputLabelProps={{
                                shrink: true,
                                style: {fontSize: isMobile ? '1.2rem' : '1.5rem'},
                              }}
                              InputProps={{
                                ...endProps.InputProps,
                                sx: {height: isMobile ? 36 : 48, fontSize: isMobile ? '1.2rem' : '1.5rem'},
                              }}
                            />
                          </div>
                        )}
                      />
                    </LocalizationProvider>
                    <Button
                      variant='contained'
                      color='error'
                      sx={{height: isMobile ? 36 : 48}}
                      onClick={async () => {
                        const today = dayjs()
                        const oneWeekAgo = today.subtract(7, 'day')
                        setFilter({
                          ...filter,
                          startDate: dayjs().subtract(7, 'day').format('YYYYMMDD'),
                          endDate: dayjs().format('YYYYMMDD'),
                        })
                        setDate([oneWeekAgo, today])
                      }}
                    >
                      <RotateLeftTwoToneIcon
                        sx={{width: isMobile ? 18 : 24, height: isMobile ? 18 : 24}}
                      />
                    </Button>
                    <Button
                      variant='contained'
                      sx={{height: isMobile ? 36 : 48}}
                      onClick={() => getImageList()}
                    >
                      <SearchIcon sx={{width: isMobile ? 18 : 24, height: isMobile ? 18 : 24}} />
                    </Button>
                    <FormControl
                      sx={{minWidth: isMobile ? 100 : 120, height: isMobile ? 36 : 48}}
                      size='small'
                    >
                      {!isMobile && <InputLabel id='select-label'>기준 선택</InputLabel>}
                      <Select
                        labelId='select-label'
                        id='select'
                        value={selectedOption}
                        label={isMobile ? undefined : '기준 선택'}
                        onChange={(e) => setSelectedOption(e.target.value)}
                        sx={{height: isMobile ? 36 : 48}}
                      >
                        <MenuItem value='reading'>검침값</MenuItem>
                        <MenuItem value='usage'>사용량</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant='outlined'
                      color='success'
                      startIcon={
                        <ListAltIcon sx={{width: isMobile ? 18 : 24, height: isMobile ? 18 : 24}} />
                      }
                      sx={{height: isMobile ? 36 : 48, textTransform: 'none'}}
                      onClick={() => getDownloadData()}
                    >
                      {isMobile ? 'Excel 다운' : 'Download'}
                    </Button>
                  </div>

                  {/* 차트 */}
                  <div style={{height: isMobile ? '200px' : '230px', flexShrink: 0, overflow: 'hidden'}}>
                    {imageList && (
                      <CustomerDetailChartFix
                        className='w-full px-3'
                        data={data}
                        date={date}
                        imageList={imageList}
                        filter={filter}
                        selectedOption={selectedOption}
                        chartHeight={isMobile ? 170 : 190}
                      />
                    )}
                  </div>

                  {/* 테이블 / 카드 */}
                  {isMobile ? (
                    <div className='flex flex-col' style={{height: '65vh'}}>
                      <div
                        ref={mobileListRef}
                        className='flex-1 overflow-y-auto flex flex-col gap-2'
                      >
                        {imageList === null ? (
                          <div className='flex justify-center p-8'>
                            <span>로딩 중...</span>
                          </div>
                        ) : (
                          rows
                            .slice(mobilePage * pageSize, (mobilePage + 1) * pageSize)
                            .map((row: any) => {
                              const imageSrc = row.filePath
                                ? `${envImagePath}${row.filePath}`
                                : null
                              const isWeakSignal =
                                row.signalStrength !== null &&
                                row.signalStrength < globalConst.rssiWarningdBm
                              const isWeakQuality =
                                row.linkquality !== null &&
                                row.linkquality < globalConst.rsrqGood
                              return (
                                <div
                                  key={row.id}
                                  className='border border-gray-200 rounded-lg p-3 bg-white shadow-sm flex gap-3'
                                >
                                  {/* 텍스트 정보 */}
                                  <div className='flex flex-col gap-1 flex-1 min-w-0'>
                                    <div className='font-bold text-gray-800'>
                                      <div className='text-xl'>
                                        {formatDateOrEmpty(row.createAt, 'YY/MM/DD')}
                                      </div>
                                      <div className='text-xl'>
                                        {formatDateOrEmpty(row.createAt, 'HH시 mm분 ss초')}
                                      </div>
                                    </div>
                                    <div className='flex gap-3 flex-wrap mt-1'>
                                      <div className='flex items-center gap-2'>
                                        <span className='text-lg text-gray-600'>전파세기</span>
                                        <span
                                          className={`text-lg ${
                                            isWeakSignal
                                              ? 'text-orange-500 font-bold'
                                              : 'text-gray-800'
                                          }`}
                                        >
                                          {row.signalStrength !== null
                                            ? `${row.signalStrength}dBm`
                                            : '-'}
                                        </span>
                                      </div>
                                      <div className='flex items-center gap-2'>
                                        <span className='text-lg text-gray-600'>전파품질</span>
                                        <span
                                          className={`text-lg ${
                                            isWeakQuality
                                              ? 'text-orange-500 font-bold'
                                              : 'text-gray-800'
                                          }`}
                                        >
                                          {row.linkquality !== null
                                            ? `${row.linkquality}dB`
                                            : '-'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  {/* 이미지 */}
                                  {imageSrc && data?.deviceType !== 'digital' && (
                                    <div
                                      className='shrink-0 flex items-center'
                                      style={{width: '50%'}}
                                    >
                                      <img
                                        src={imageSrc}
                                        alt='check'
                                        className='w-full object-contain rounded border border-gray-200'
                                        style={{
                                          maxHeight: 100,
                                          transform: `rotate(${row.rotate >= 90 && row.rotate <= 270 ? 180 : 0}deg)`,
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              )
                            })
                        )}
                      </div>
                      {/* 페이지네이션 */}
                      <div className='flex items-center justify-between border-t pt-2 mt-1 shrink-0'>
                        <FormControl size='small' sx={{minWidth: 80}}>
                          <Select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value))
                              setMobilePage(0)
                            }}
                          >
                            {[10, 20, 60, 100].map((s) => (
                              <MenuItem key={s} value={s}>
                                {s}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <span className='text-sm text-gray-600'>
                          {mobilePage * pageSize + 1}–
                          {Math.min((mobilePage + 1) * pageSize, rows.length)} / {rows.length}
                        </span>
                        <div className='flex gap-1'>
                          <Button
                            size='small'
                            variant='outlined'
                            disabled={mobilePage === 0}
                            onClick={() => {
                              setMobilePage((p) => p - 1)
                              if (mobileListRef.current) mobileListRef.current.scrollTop = 0
                            }}
                          >
                            ‹
                          </Button>
                          <Button
                            size='small'
                            variant='outlined'
                            disabled={(mobilePage + 1) * pageSize >= rows.length}
                            onClick={() => {
                              setMobilePage((p) => p + 1)
                              if (mobileListRef.current) mobileListRef.current.scrollTop = 0
                            }}
                          >
                            ›
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Box id='table-content' sx={{flex: 1, minHeight: 0, width: '100%'}}>
                      <DataGridPro
                        rows={rows}
                        columns={columns}
                        rowHeight={110}
                        getRowHeight={(params: any) => getRowHeightOnDataGrid(params)}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        rowsPerPageOptions={[10, 20, 60, 100]}
                        disableColumnMenu
                        pagination
                        loading={imageList === null}
                      />
                    </Box>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  )
}
export default DeviceTargetImageListModal
